import type { Agent } from '@flatfile/api-internal/api'
import {
  Button,
  DialogHeader,
  Drawer,
  DrawerContent,
  DrawerPortal,
  DrawerTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconName,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Switch,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@flatfile/baseline'
import { useEffect, useState } from 'react'
import { getLogByCompoundKey } from '../../api/overrides/logs'
import { ResourceList } from '../../components/ResourceList/ResourceList'
import { ResourceListContextProvider } from '../../components/ResourceList/ResourceListContext'
import { ListPagination, ResourceRowComponent } from '../../components/ResourceList/types'
import { CustomCopyButton } from '../../components/Shared/CopyToClipboard'
import { NoAccessError } from '../../components/Shared/CustomErrorComponent'
import TrialBanner from '../../components/Shared/TrialBanner'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import useResource from '../../resources/hooks/useResource'
import useResourceList from '../../resources/hooks/useResourceList'
import { formatDate, formatTime, getFormattedDuration } from '../../utils/format'

// Map of Baseline icon names for different topics
const getIconForTopic = (topic: string): IconName => {
  const topicBase = topic.split(':')[0]
  switch (topicBase) {
    case 'agent':
      return 'eye-open'
    case 'space':
      return 'rocket'
    case 'document':
      return 'folder-2'
    case 'workbook':
      return 'book'
    case 'sheet':
      return 'page-text'
    case 'snapshot':
      return 'camera-1'
    case 'records':
      return 'list-bullets'
    case 'file':
      return 'file-text'
    case 'job':
      return 'truck'
    case 'program':
      return 'bezier-curve'
    case 'commit':
      return 'commit'
    case 'layer':
      return 'dots-grid-2x3'
    case 'secret':
      return 'key-2'
    case 'upload':
      return 'cloud-simple-download'
    case 'client':
      return 'people'
    case 'action':
      return 'live-activity'
    default:
      return 'play'
  }
}

const prettyPrint = (input: any): string => {
  if (typeof input === 'string') {
    try {
      return JSON.stringify(JSON.parse(input), null, 2)
    } catch (error) {
      return input
    }
  } else {
    try {
      return JSON.stringify(input, null, 2)
    } catch (error) {
      return String(input)
    }
  }
}

const LogRow: ResourceRowComponent = ({ resourceId }) => {
  const [log, setLog] = useState<any>(null) // TODO: Find a better way to do this! Currently not using useResource because executions doesn't bring back all log by design to prevent a slow query
  const [opened, setOpened] = useState(false)
  const execution = useResource('LOGS', resourceId)

  const openEvent = () => {
    if (!opened) {
      setOpened(true)
    }
  }

  useEffect(() => {
    const fetchLog = async () => {
      const { data } = await getLogByCompoundKey(resourceId)
      const logData = {
        ...execution,
        context: data.context,
        log: data.log,
      }
      setLog(logData)
    }
    fetchLog()
  }, [resourceId, execution])

  if (!log) {
    return <></>
  }

  return (
    <tr className='hover:bg-gray-50 rounded-md overflow-hidden p-1'>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          {log.success ? (
            <Icon name='circle-check' className='text-success' size={16} />
          ) : (
            <a href='https://flatfile.com/docs/orchestration/listeners#failures' target='_blank' rel='noreferrer'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Icon name='triangle-exclamation' className='text-destructive' size={16} />
                </TooltipTrigger>
                <TooltipContent className='flex items-center px-2 py-1'>
                  <div>
                    <Text variant='body' className='text-sm font-bold'>
                      Executing this event failed.
                    </Text>
                    <Text variant='body' className='text-sm block mt-1'>
                      Click to learn more.
                    </Text>
                  </div>
                </TooltipContent>
              </Tooltip>
            </a>
          )}
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button className='text-primary px-0 h-auto flex items-center gap-1' variant='ghost' onClick={openEvent}>
                <div className='flex items-center gap-1.5'>
                  <Icon name={getIconForTopic(log.topic)} size={12} />
                  <Text className='text-sm'>{log.topic}</Text>
                  <Icon name='sidebar-right-arrow' className='text-gray-300' size={12} />
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent className='flex items-center px-2 py-1'>
              <Text variant='body' className='text-sm'>
                Open in side peek
              </Text>
            </TooltipContent>
          </Tooltip>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <CustomCopyButton value={log.eventId} />
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <CustomCopyButton value={log.agentId} />
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <CustomCopyButton value={log.spaceId || 'N/A'} />
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text className='text-sm'>
            {formatDate(log.createdAt)} {formatTime(log.createdAt, true)}
          </Text>
        </div>
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200'>
        <div className='flex items-center h-full'>
          <Text className='text-sm'>{getFormattedDuration(undefined, undefined, log.duration)}</Text>
        </div>
        <Drawer open={opened} onOpenChange={setOpened}>
          <DrawerPortal>
            <DrawerContent className='w-full max-w-xl overflow-auto bg-[#FFF]' side='right'>
              <div className='p-4'>
                <DialogHeader>
                  <DrawerTitle>Event Detail</DrawerTitle>
                </DialogHeader>
                <div className='mt-4 space-y-6'>
                  <div>
                    <h3 className='text-lg font-medium'>{log.topic}</h3>
                    <CustomCopyButton value={log.eventId} />
                  </div>
                  <div>
                    <h4 className='text-base font-medium mb-2'>Context</h4>
                    <pre className='bg-gray-100 p-3 rounded-md text-xs overflow-auto whitespace-pre-wrap'>
                      {JSON.stringify(log.context, null, 2)}
                    </pre>
                  </div>
                  <div>
                    <h4 className='text-base font-medium mb-2'>Console Log</h4>
                    {log.log ? (
                      <pre className='bg-gray-100 p-3 rounded-md text-xs overflow-auto whitespace-pre-wrap'>
                        {prettyPrint(log.log)}
                      </pre>
                    ) : (
                      <Text className='text-sm text-muted'>There are no logs for this event.</Text>
                    )}
                  </div>
                </div>
              </div>
            </DrawerContent>
          </DrawerPortal>
        </Drawer>
      </td>
    </tr>
  )
}

const LogsHeader = () => {
  return (
    <thead className='border-b border-gray-200'>
      <tr>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'></Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Topic
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Event ID
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Agent ID
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Space ID
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]'>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Timestamp
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: '100px' }}>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Duration
          </Text>
        </th>
      </tr>
    </thead>
  )
}

export const LogsPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const pagination: ListPagination = { currentPage, setCurrentPage, pageCount, setPageCount }
  const [showFailures, setShowFailures] = useState(false)
  const { hasEnvAccess } = useActiveEnvironment()
  const { addToRefsArray } = useDynamicHeight()

  const { environment: activeEnvironment } = useActiveEnvironment()
  const agentData = useResourceList('AGENTS', { environmentId: activeEnvironment.id }).response.data
  const [agents, setAgents] = useState(agentData || [])
  const [selectedAgent, setSelectedAgent] = useState<Agent | 'All'>('All')

  useEffect(() => {
    if (agentData) {
      setAgents(agentData)
    }
  }, [agentData])

  const handleAgentChange = (value: string | null) => {
    if (!value || value === 'All') {
      setSelectedAgent('All')
    } else {
      const targetAgent = agents.find((agent) => agent.slug === value)
      if (targetAgent) {
        setSelectedAgent(targetAgent)
      }
    }
    setCurrentPage(1)
  }

  if (!hasEnvAccess) return <NoAccessError page='Logs.page.tsx' />
  return (
    <div ref={addToRefsArray} className='w-full h-full flex flex-col p-3'>
      <div className='p-3 flex justify-between'>
        <Text variant='title2'>Event Logs</Text>
        <TrialBanner />
      </div>

      <div>
        <div className='flex justify-between items-center mx-2 py-4 h-full'>
          <div className='flex items-center gap-4'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='outline'
                  icon='chevron-down-small'
                  iconPlacement='right'
                  iconProps={{ size: 12 }}
                  size='sm'
                >
                  <Text className='text-sm'>{selectedAgent === 'All' ? 'All Agents' : selectedAgent.slug}</Text>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='start'>
                <DropdownMenuItem onClick={() => handleAgentChange('All')}>All Agents</DropdownMenuItem>
                {agents.map((agent) => (
                  <DropdownMenuItem key={agent.id} onClick={() => handleAgentChange(agent.slug || '')}>
                    {agent.slug}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className='flex items-center gap-2'>
              <Switch id='failures-only' checked={showFailures} onCheckedChange={setShowFailures} />
              <label htmlFor='failures-only' className='text-sm whitespace-nowrap'>
                Failures Only
              </label>
            </div>
          </div>
          <Pagination className='justify-end' iconOnly size='sm'>
            <PaginationContent>
              <PaginationPrevious
                onClick={() => {
                  if (currentPage > 1) setCurrentPage(currentPage - 1)
                }}
                disabled={currentPage <= 1}
              />
              {(() => {
                const maxVisiblePages = 5
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                // Always show at least one page
                const effectivePageCount = Math.max(1, pagination.pageCount)
                const endPage = Math.min(effectivePageCount, startPage + maxVisiblePages - 1)

                // Adjust start page if we're near the end
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1)
                }

                // Generate array of page numbers to display
                const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

                return pages.map((pageNum) => (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      isActive={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                      className={cn(
                        'w-5 h-5 flex items-center justify-center rounded-full p-0',
                        pageNum === currentPage
                          ? 'bg-surface-sentiment-subtle-neutral border-none'
                          : 'hover:bg-gray-100'
                      )}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                ))
              })()}
              <PaginationNext
                onClick={() => {
                  if (currentPage < pagination.pageCount) setCurrentPage(currentPage + 1)
                }}
                disabled={currentPage >= pagination.pageCount || pagination.pageCount <= 1}
              />
            </PaginationContent>
          </Pagination>
        </div>
      </div>
      <div className='relative'>
        <div className='overflow-x-auto h-full relative'>
          <ResourceListContextProvider
            requestParams={{
              ...(showFailures ? { sortDirection: 'desc', success: false } : { sortDirection: 'desc' }),
              ...(selectedAgent && selectedAgent !== 'All' ? { agentId: selectedAgent.id } : {}),
            }}
            resourceType='LOGS'
          >
            <ResourceList
              pagination={pagination}
              headerComponent={LogsHeader}
              rowComponent={LogRow}
              errorMessage='Error loading logs.'
              emptyMessage='No logs to display in this environment.'
            />
          </ResourceListContextProvider>
        </div>
      </div>
    </div>
  )
}
